import React, { useState, useEffect, useContext } from "react";
import {
  FiHome,
  FiUsers,
  FiUserCheck,
  FiShield,
  FiArrowRight,
  FiArrowUp,
  FiArrowDown,
  FiMapPin,
} from "react-icons/fi";
import '../../../assets/css/sellers/dashboard/sellerdashboard/sellersdashboard.css';
import axios from "axios";
import SellerMasterLayout from "layouts/SellerMasterLayout";
import AppContext from "context/AppContext";
import KYCModel from "../kycmodal/KYCModel";

// ── Helpers ──────────────────────────────────────────────────
const STATUS_MAP = {
  Received: { bg: "var(--info-color)", text: "#ffffff" },
  Pending: { bg: "var(--warning-color)", text: "#5a4400" },
  Rejected: { bg: "var(--error-color)", text: "#ffffff" },
  Approved: { bg: "var(--success-color)", text: "#ffffff" },
  "Under Review": { bg: "var(--info-color)", text: "#ffffff" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || { bg: "var(--bg-light-gray)", text: "var(--text-secondary)" };
  return (
    <span className="sd-badge" style={{ background: s.bg, color: s.text }}>
      {status}
    </span>
  );
};

// ── Component ────────────────────────────────────────────────
const SellerDashboard = () => {
  const { baseUrl, baseUrlForFiles, Navigate, authToken } = useContext(AppContext);

  const [dashboardStats, setDashboardStats] = useState({
    totalProperties: 0,
    visitRequests: 0,
    totalLeads: 0,
    kycCount: 0,
    propertiesPercentage: 0,
    visitRequestsPercentage: 0,
    leadsPercentage: 0,
    kycPercentage: 0,
  });

  const [recentProperties, setRecentProperties] = useState([]);
  const [visitRequests, setVisitRequests] = useState([]);
  const [kycRequests, setKycRequests] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [animateCards, setAnimateCards] = useState(false);
  const [showKycModal, setShowKycModal] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);
  const [hasKyc, setHasKyc] = useState(false);

  const KYCStatusBadge = () => {
    if (!hasKyc) {
      return (
        <span className="sd-kyc-status sd-kyc-not-verified">
          KYC Not Completed
        </span>
      );
    }
    if (kycStatus === "Approved") {
      return (
        <span className="sd-kyc-status sd-kyc-verified">
          <FiUserCheck size={15} />
          Verified
        </span>
      );
    }
    if (kycStatus === "Pending" || kycStatus === "Under Review") {
      return (
        <span className="sd-kyc-status sd-kyc-pending">
          KYC Under Review
        </span>
      );
    }
    if (kycStatus === "Rejected") {
      return (
        <span className="sd-kyc-status sd-kyc-rejected">
          KYC Rejected
        </span>
      );
    }
    return null;
  };
  // ── Stat cards ────────────────────────────────────────────
  const getDashboardStats = () => {
    axios
      .get(`${baseUrl}/seller/dashboard/dashboardStats`, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      })
      .then((response) => {
        setDashboardStats({
          totalProperties: response.data.totalProperties,
          visitRequests: response.data.visitRequests,
          totalLeads: response.data.totalLeads,
          kycCount: response.data.kycCount,
          propertiesPercentage: response.data.propertiesPercentage,
          visitRequestsPercentage: response.data.visitRequestsPercentage,
          leadsPercentage: response.data.leadsPercentage,
          kycPercentage: response.data.kycPercentage,
        });
      })
      .catch((error) => console.log(error));
  };
  // ------------- Check logged-in seller's KYC status -----------
 const getMyKycStatus = () => {
  axios
    .get(`${baseUrl}/seller/sellerkyc/getMyKycStatus`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
    .then((response) => {
      if (response.data.success) {

        const { hasKyc, status } = response.data;

        setHasKyc(hasKyc);
        setKycStatus(status);

        // Seller has not submitted KYC
        if (!hasKyc) {
          setShowKycModal(true);
          return;
        }

        // KYC exists — don't show first-login modal
        setShowKycModal(false);
      }
    })
    .catch((error) => {
      console.error("Error checking KYC status:", error);
    });
};
  // ── Recently added properties ───────────────────────────────
  const getRecentProperties = () => {
    axios
      .get(`${baseUrl}/seller/dashboard/recentProperties`, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      })
      .then((response) => setRecentProperties(response.data.properties))
      .catch((error) => console.log("Error fetching properties:", error));
  };

  // ── Recent visit requests ───────────────────────────────────
  const getVisitRequests = () => {
    axios
      .get(`${baseUrl}/seller/dashboard/visitRequests`, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      })
      .then((response) => setVisitRequests(response.data.visits))
      .catch((error) => console.log("Error fetching visit requests:", error));
  };

  // ── Recently added KYC ───────────────────────────────────────
  const getKycRequests = () => {
    axios
      .get(`${baseUrl}/seller/dashboard/kycRequests`, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      })
      .then((response) => setKycRequests(response.data.kyc))
      .catch((error) => console.log("Error fetching KYC:", error));
  };

  // ── Recent conversations ─────────────────────────────────────
  const getConversations = () => {
    axios
      .get(`${baseUrl}/seller/dashboard/conversations`, {
        headers: {
          "Authorization": `Bearer ${authToken}`
        },
      })
      .then((response) => setConversations(response.data.conversations))
      .catch((error) => console.log("Error fetching conversations:", error));
  };

  useEffect(() => {
    if (!authToken) return;
    setAnimateCards(true);
    getDashboardStats();
    getRecentProperties();
    getVisitRequests();
    getKycRequests();
    getConversations();
    getMyKycStatus();
  }, [authToken]);

  const statsData = [
    {
      id: 1,
      title: "Total Properties",
      value: dashboardStats.totalProperties,
      change: dashboardStats.propertiesPercentage,
      icon: <FiHome />,
      bg: "var(--success-color)",
      bgSoft: "#e8f7ee",
    },
    {
      id: 2,
      title: "Visit Requests",
      value: dashboardStats.visitRequests,
      change: dashboardStats.visitRequestsPercentage,
      icon: <FiUsers />,
      bg: "var(--info-color)",
      bgSoft: "#e6f4f8",
    },
    {
      id: 3,
      title: "Total Leads",
      value: dashboardStats.totalLeads,
      change: dashboardStats.leadsPercentage,
      icon: <FiUserCheck />,
      bg: "var(--warning-color)",
      bgSoft: "#fff8e1",
    },
    {
      id: 4,
      title: "Recently Added KYC",
      value: dashboardStats.kycCount,
      change: dashboardStats.kycPercentage,
      icon: <FiShield />,
      bg: "var(--secondary-color)",
      bgSoft: "#f0f0f0",
    },
  ];

  return (
    <SellerMasterLayout pageTitle="Dashboard">
      <div className="sd-page">
        {/* ══ SELLER KYC STATUS ═══════════════════════════════ */}
        <div className="sd-kyc-status-card">

          <div className="sd-kyc-status-info">

            <div className="sd-kyc-status-icon">
              <FiShield size={22} />
            </div>

            <div>
              <h3 className="sd-kyc-status-title">
                KYC Verification
              </h3>

              <p className="sd-kyc-status-description">
                Your seller verification status
              </p>
            </div>

          </div>

          <div className="sd-kyc-status-right">
            <KYCStatusBadge />

            {kycStatus !== "Approved" && (
              <button
                className="sd-complete-kyc-btn"
                onClick={() => Navigate("/seller/kyc")}
              >
                {kycStatus === "Rejected"
                  ? "Resubmit KYC"
                  : "Complete KYC"}
                <FiArrowRight size={14} />
              </button>
            )}
          </div>

        </div>
        {/* ══ STAT CARDS ══════════════════════════════════ */}
        <div className="sd-stats-grid">
          {statsData.map((s, i) => (
            <div
              key={s.id}
              className={`sd-stat-card ${animateCards ? "sd-animate" : ""}`}
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="sd-stat-top">
                <div className="sd-stat-icon-wrap" style={{ background: s.bgSoft }}>
                  <span className="sd-stat-icon" style={{ color: s.bg }}>
                    {s.icon}
                  </span>
                </div>
              </div>
              <p className="sd-stat-label">{s.title}</p>
              <h2 className="sd-stat-value">{s.value}</h2>
              <div className="sd-stat-change">
                <span
                  className={`sd-change-num ${s.change < 0 ? "sd-change-neg" : "sd-change-pos"
                    }`}
                >
                  {s.change < 0 ? <FiArrowDown size={12} /> : <FiArrowUp size={12} />}
                  {Math.abs(s.change)}%
                </span>
                <span className="sd-change-text">this month</span>
              </div>
            </div>
          ))}
        </div>

        {/* ══ RECENTLY ADDED PROPERTIES ═══════════════════ */}
        <div className="sd-card sd-full-width">
          <div className="sd-section-header">
            <h3 className="sd-section-title">Recently Added Properties</h3>
            <button
              className="sd-view-all-btn"
              onClick={() => Navigate("/seller/my-property")}
            >
              View All Properties <FiArrowRight size={14} />
            </button>
          </div>
          <div className="sd-property-grid">
            {recentProperties.map((p) => (
              <div key={p.id} className="sd-property-card">
                <div className="sd-property-img-wrap">
                  <img
                    src={`${baseUrlForFiles}/${p.expropertyimages?.[0]?.images}`}
                    alt={p.property_title}
                    className="sd-property-img"
                  />
                  <span
                    className={`sd-status-badge ${p.status !== "Active" ? "sd-status-inactive" : ""
                      }`}
                  >
                    {p.status}
                  </span>
                </div>
                <div className="sd-property-body">
                  <p className="sd-property-title">{p.property_title}</p>
                  <p className="sd-property-loc">
                    <FiMapPin size={11} /> {p.location}
                  </p>
                  <p className="sd-property-added">{new Date(p.created_at).toLocaleDateString()}</p>
                  <p className="sd-property-price">{p.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ MAIN GRID ════════════════════════════════════ */}
        <div className="sd-main-grid">
          {/* Recent Visit Requests */}
          <div className="sd-card">
            <div className="sd-section-header">
              <h3 className="sd-section-title">Recent Visit Requests</h3>
              <button
                className="sd-view-all-btn"
                onClick={() => Navigate("/seller/visit-requests")}
              >
                View All Requests <FiArrowRight size={14} />
              </button>
            </div>
            <div className="sd-table-wrap">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th>Buyer Name</th>
                    <th>Property</th>
                    <th>Date &amp; Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visitRequests.map((v) => (
                    <tr key={v.id} className="sd-table-row">
                      <td>
                        <div className="sd-buyer-cell">
                          <img
                            src={`${baseUrlForFiles}${v.exbuyer?.profileImage}`}
                            alt={v.exbuyer?.firstName}
                            className="sd-avatar"
                          />
                          <p className="sd-buyer-name">
                            {v.exbuyer?.firstName} {v.exbuyer?.lastName}
                          </p>
                        </div>
                      </td>
                      <td>
                        <p className="sd-buyer-name">{v.exproperty.property_title}</p>
                        <p className="sd-property-sub">{v.exproperty.location}</p>
                      </td>
                      <td className="sd-datetime">
                        {new Date(v.bookingDate).toLocaleString()}
                      </td>
                      <td>
                        <StatusBadge status={v.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recently Added KYC */}
          <div className="sd-card">
            <div className="sd-section-header">
              <h3 className="sd-section-title">Recently Added KYC</h3>
              <button
                className="sd-view-all-btn"
                onClick={() => Navigate("/seller/kyc")}
              >
                View All KYC <FiArrowRight size={14} />
              </button>
            </div>
            <div className="sd-table-wrap">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th>Seller Name</th>
                    <th>Submitted On</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {kycRequests.map((k) => (
                    <tr key={k.id} className="sd-table-row">
                      <td>
                        <div className="sd-buyer-cell">
                          <img
                            src={`${baseUrlForFiles}${k.exseller?.profileImage}`}
                            alt={k.exseller?.firstName}
                            className="sd-avatar"
                          />
                          <p className="sd-buyer-name">
                            {k.exseller?.firstName} {k.exseller?.lastName}
                          </p>
                        </div>
                      </td>
                      <td className="sd-datetime">
                        {new Date(k.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <StatusBadge status={k.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ══ RECENT CONVERSATIONS ═════════════════════════ */}
        <div className="sd-card sd-full-width">
          <div className="sd-section-header">
            <h3 className="sd-section-title">Recent Conversations</h3>
            <button
              className="sd-view-all-btn"
              onClick={() => Navigate("/seller/messages")}
            >
              Go to Messages <FiArrowRight size={14} />
            </button>
          </div>
          <div className="sd-table-wrap">
            <table className="sd-table">
              <thead>
                <tr>
                  <th>Buyer</th>
                  <th>Last Message</th>
                  <th>Time</th>
                  <th>Unread</th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((c) => (
                  <tr key={c.id} className="sd-table-row">
                    <td>
                      <div className="sd-buyer-cell">
                        <img
                          src={`${baseUrlForFiles}${c.exbuyer?.profileImage}`}
                          alt={c.exbuyer?.firstName}
                          className="sd-avatar"
                        />
                        <p className="sd-buyer-name">
                          {c.exbuyer?.firstName} {c.exbuyer?.lastName}
                        </p>
                      </div>
                    </td>
                    <td className="sd-property-sub">{c.last_message}</td>
                    <td className="sd-datetime">
                      {new Date(c.created_at).toLocaleString()}
                    </td>
                    <td>
                      <span
                        className={
                          c.unread_count > 0 ? "sd-unread" : "sd-unread-zero"
                        }
                      >
                        {c.unread_count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <KYCModel
        isOpen={showKycModal}
        onCompleteKyc={() => Navigate("/seller/kyc")}
        onSkip={() => setShowKycModal(false)}
      />
    </SellerMasterLayout>
  );
};

export default SellerDashboard;